import { useEffect, useSyncExternalStore } from 'react';
import { creerClient, type Client } from '../../api/client.js';
import type { RoleCode } from '../../api/contrat.js';
import { modeMocks } from '../../app/entree.js';

/**
 * Couloir G, point 1 : mode de travail de l'utilisateur (« guide » ou « avance »), gardé par le serveur pour chaque utilisateur
 * (`demarrage.mode.lire`, `demarrage.mode.choisir`). Lu une fois par session et partagé par la coquille et les écrans ; tant qu'il
 * n'est pas lu, rien ne change à l'écran (l'application avancée reste affichée).
 */
export type ModeTravail = 'guide' | 'avance';
export interface EtatMode {
  /** `inconnu` : pas encore lu (ou lecture impossible) ; `lu` : `mode` vaut le choix enregistré, null s'il n'y en a pas. */
  statut: 'inconnu' | 'lu';
  mode: ModeTravail | null;
}

/** Rôles qui choisissent leur mode : Isabelle (dirigeante ou administration) et Brigitte. */
export const ROLES_MODE: ReadonlySet<RoleCode> = new Set<RoleCode>(['dirigeante', 'admin', 'assistante']);

const INCONNU: EtatMode = { statut: 'inconnu', mode: null };
let etat: EtatMode = INCONNU;
let utilisateur: string | null = null;
let lecture: Promise<void> | null = null;
const abonnes = new Set<() => void>();
const changer = (suite: EtatMode) => {
  etat = suite;
  for (const f of abonnes) f();
};

const clientParDefaut = (): Pick<Client, 'appeler'> => creerClient({ fetch: (...a) => globalThis.fetch(...a) });

/** Oublie le mode lu (changement de session, tests). */
export function oublierModeTravail(): void {
  utilisateur = null;
  lecture = null;
  changer(INCONNU);
}

export function lireModeTravail(userId: string, client: Pick<Client, 'appeler'> = clientParDefaut()): Promise<void> {
  if (utilisateur === userId && lecture) return lecture;
  utilisateur = userId;
  etat = INCONNU;
  lecture = client.appeler<{ mode: ModeTravail | null }>('demarrage.mode.lire').then(
    (r) => {
      if (utilisateur === userId) changer({ statut: 'lu', mode: r.mode });
    },
    () => undefined,
  );
  return lecture;
}

export async function choisirModeTravail(mode: ModeTravail, client: Pick<Client, 'appeler'> = clientParDefaut()): Promise<boolean> {
  try {
    const r = await client.appeler<{ mode: ModeTravail }>('demarrage.mode.choisir', { corps: { mode } });
    changer({ statut: 'lu', mode: r.mode });
    return true;
  } catch {
    return false;
  }
}

/** Mode de l'utilisateur courant ; lu au premier rendu (jamais pendant un rendu serveur). */
export function useModeTravail(userId: string | null, role: RoleCode | null): EtatMode {
  // En développement sur les mocks, aucun appel réseau global : le mode n'est pas lu (l'application avancée reste affichée).
  const concerne = userId !== null && role !== null && ROLES_MODE.has(role) && !modeMocks(import.meta.env);
  useEffect(() => {
    if (concerne) void lireModeTravail(userId);
  }, [concerne, userId]);
  const lu = useSyncExternalStore(
    (f) => {
      abonnes.add(f);
      return () => void abonnes.delete(f);
    },
    () => etat,
    () => INCONNU,
  );
  return concerne && utilisateur === userId ? lu : INCONNU;
}
